import React, { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";

import { convertFullDateTimeToWithoutYearSecond } from "../../../../libs/dateFormat";
import { cutNameForView, maskingName } from "../../../../libs/common";

import {
  MAX_NICKNAME_LENGTH,
  MIN_COMMENT_LENGTH,
} from "../../../../consts/common";

import { UserState, useUserContext } from "../../../../contexts/userContext";

import { SatisfactionInfo } from "../../../../services/spaceReservationServices";
import {
  patchEtcSatisfactionReply,
  postEtcSatisfactionReply,
} from "../../../../services/etcServices";

import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../../hooks/useSimplePopup";

import StarScore from "../../../Talk/StarScore";
import CommonButton from "../../../Button";
import HorizontalRule from "../../../Rule/Horizontal";
import CommonTextArea from "../../../../components/TextArea";
import CommonProfile from "../../../MyInfo/Profile";

type Props = {
  review: SatisfactionInfo;
  loadReview: () => void;
  useNickname?: boolean;
};

export default function RentalEachReview({
  review,
  loadReview,
  useNickname,
}: Props) {
  const [user] = useUserContext();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2.5">
        <CommonProfile src={review?.createdByImage} />
        <div className="flex justify-between w-full">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-[5px]">
              <div>
                <StarScore score={review.satisfaction ?? 0} />
              </div>
              <div className=" text-sm text-brand-neutral-85 font-semibold">
                {review.createdByName ?? ""} {review.rental}
              </div>
            </div>
            <div className="text-base">{review.comment}</div>
          </div>
          <div className="text-xs text-[#999999]">
            {convertFullDateTimeToWithoutYearSecond(review.createdAt).replace(
              ".",
              "/"
            )}
          </div>
        </div>
      </div>

      <RentalEachReviewReply
        review={review}
        reply={review?.reply}
        user={user}
        loadReview={loadReview}
        useNickname={useNickname}
      />

      <HorizontalRule margin="0" />
    </div>
  );
}

export type RentalEachReviewReplyProps = {
  review: SatisfactionInfo;
  reply?: SatisfactionInfo;
  user: UserState;
  loadReview: () => void;
  useNickname?: boolean;
};

export const RentalEachReviewReply = ({
  review,
  reply,
  user,
  loadReview,
  useNickname,
}: RentalEachReviewReplyProps) => {
  const [content, setContent] = useState<string>(reply?.comment ?? "");
  const [viewComment, setViewComment] = useState<string>(reply?.comment ?? ""); // 수정 중 노출
  const [finalComment, setFinalComment] = useState<string>(
    reply?.comment ?? ""
  ); // 최신 comment. 수정 시 데이터를 새로 가져오지 않으므로 필요.
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open, openError }] = useSimplePopup({});

  const getName = () => {
    let defaultName = "";

    if (reply) {
      if (useNickname) {
        defaultName = reply?.createdByNickName ?? "";
      } else {
        defaultName = reply?.createdByName ?? "";
      }
    } else {
      if (useNickname) {
        defaultName = user?.nickname ?? "";
      } else {
        defaultName = user?.name ?? "";
      }
    }

    if (!useNickname) {
      defaultName = maskingName(defaultName);
    }

    if (defaultName.length > MAX_NICKNAME_LENGTH) {
      defaultName = cutNameForView(defaultName, MAX_NICKNAME_LENGTH);
    }

    return defaultName;
  };

  useEffect(() => {
    setContent(reply?.comment ?? "");
  }, [reply?.comment]);

  return (
    <>
      {loadingComponent}
      {reply ? (
        <div className="pl-[50px]">
          <div className="p-[15px] rounded-[5px] flex flex-col gap-2.5 bg-[#f5f5f5]">
            <div className="flex gap-[5px] items-center">
              <CommonProfile
                src={reply?.createdByImage ?? undefined}
                sizeVariant="reply"
              />
              <div
                className="flex-1 text-sm font-medium"
                data-testid="reply-name"
              >
                {getName()}
              </div>
              <div className="text-xs text-[#999999]">
                {convertFullDateTimeToWithoutYearSecond(
                  reply?.createdAt
                ).replace(".", "/")}
              </div>
            </div>
            {editMode !== true && <div>{finalComment}</div>}
            {editMode === true ? (
              <div className="relative">
                <CommonTextArea
                  placeholder={`이용자의 평가에 대한 댓글을 남겨주세요\n(${MIN_COMMENT_LENGTH}자 이상)`}
                  value={viewComment}
                  onChange={(e) => {
                    setViewComment(e.target.value);
                  }}
                />
                <div className="absolute bottom-2.5 right-2.5 flex gap-[5px]">
                  <CommonButton
                    variant="tertiary"
                    className="px-4 py-2"
                    onClick={async () => {
                      setEditMode(false);
                      setViewComment(finalComment);
                    }}
                  >
                    취소
                  </CommonButton>
                  <CommonButton
                    variant="tertiary"
                    className="px-4 py-2"
                    onClick={async () => {
                      setIsLoading(true);

                      if (viewComment.length < MIN_COMMENT_LENGTH) {
                        open(
                          `댓글은 ${MIN_COMMENT_LENGTH}자 이상 입력해주세요.`
                        );
                        return;
                      }

                      try {
                        const result = await patchEtcSatisfactionReply(
                          reply?.etcSatisfactionId.toString(),
                          {
                            comment: viewComment,
                          }
                        );
                        if (result.status === HttpStatusCode.Ok) {
                          await loadReview();
                          setFinalComment(viewComment);
                          setEditMode(false);
                        } else {
                          throw new Error("댓글 수정에 실패했습니다.");
                        }
                      } catch (e: any) {
                        openError(
                          e,
                          "만족도 평가 댓글을 수정하는데 실패했습니다."
                        );
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                  >
                    수정
                  </CommonButton>
                </div>
              </div>
            ) : (
              <CommonButton
                variant="tertiary"
                className="py-2 text-brand-neutral-60 font-medium"
                onClick={() => setEditMode(true)}
              >
                수정
              </CommonButton>
            )}
          </div>
        </div>
      ) : (
        <div className="pl-[50px]">
          <div className="p-[15px] rounded-[5px] flex flex-col gap-2.5 bg-[#f5f5f5]">
            <div className="flex gap-[5px] items-center">
              <CommonProfile
                src={user?.profileImage?.filePath}
                sizeVariant="reply"
              />
              <div
                className="flex-1 text-sm font-medium"
                data-testid="reply-name"
              >
                {getName()}
              </div>
            </div>
            <div className="relative">
              <CommonTextArea
                placeholder={`이용자의 평가에 대한 댓글을 남겨주세요\n(${MIN_COMMENT_LENGTH}자 이상)`}
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                }}
              />
              <CommonButton
                variant="tertiary"
                className="absolute bottom-2.5 right-2.5 px-4 py-2"
                onClick={async () => {
                  if (content.length < MIN_COMMENT_LENGTH) {
                    open(`댓글은 ${MIN_COMMENT_LENGTH}자 이상 입력해주세요.`);
                    return;
                  }

                  setIsLoading(true);
                  try {
                    const result = await postEtcSatisfactionReply(
                      review?.serviceId ?? "",
                      {
                        serviceName: "rental",
                        comment: content,
                      }
                    );
                    if (result.status === HttpStatusCode.Ok) {
                      setContent("");
                      loadReview();
                    } else {
                      throw new Error("댓글 등록에 실패했습니다.");
                    }
                  } catch (e: any) {
                    openError(e, "만족도 평가 댓글을 등록하는데 실패했습니다.");
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                등록
              </CommonButton>
            </div>
          </div>
        </div>
      )}
      {simplePopupComponent}
    </>
  );
};
